class ProgressBar extends HTMLElement {
  #threshold = 0;
  #textEl = null;
  #fillEl = null;
  #trackEl = null;
  #templates = { empty: '', progress: '', goal: '' };
  #abortController = null;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#threshold = parseFloat(this.dataset.threshold) || 0;
    this.#textEl = this.querySelector('[data-progress-text]');
    this.#fillEl = this.querySelector('[data-progress-fill]');
    this.#trackEl = this.querySelector('.progress-bar__track');
    this.#templates = {
      empty: this.dataset.textEmpty || '',
      progress: this.dataset.textProgress || '',
      goal: this.dataset.textGoal || '',
    };

    // No initial fetch: the block renders the correct state server-side (from `cart`) on the
    // first paint, already in the request language. Fetching here would overwrite that text
    // with the data-text-* template and, on stores with translation, briefly flash the
    // untranslated (English) string before it is re-translated. Only react to real cart changes.
    //
    // delegate-exempt: these are document-level custom cart events (cart-api / cart-drawer
    // dispatch them on document, they bubble); Delegate targets element subtrees, not document.
    document.addEventListener('product:added', this.#onCartChange);
    document.addEventListener('cart:updated', this.#onCartChange);
    document.addEventListener('cart:refresh', this.#onCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener('product:added', this.#onCartChange);
    document.removeEventListener('cart:updated', this.#onCartChange);
    document.removeEventListener('cart:refresh', this.#onCartChange);
    this.#abortController?.abort();
  }

  #onCartChange = () => {
    this.#fetchCart();
  };

  async #fetchCart() {
    // Abort any in-flight request so a slower response can't overwrite a newer one.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        signal: this.#abortController.signal,
      });
      if (!res.ok) return;
      const cart = await res.json();
      this.#update(cart.total_price || 0);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('progress-bar: cart fetch failed', err);
    }
  }

  #update(cartTotalCents) {
    const thresholdCents = this.#threshold * 100;
    if (thresholdCents <= 0) return;

    const ratio = Math.min(1, cartTotalCents / thresholdCents);
    const remainingCents = Math.max(0, thresholdCents - cartTotalCents);

    let state, text;
    if (cartTotalCents === 0) {
      state = 'empty';
      text = this.#templates.empty.replace('[amount]', this.#formatMoney(thresholdCents));
    } else if (cartTotalCents >= thresholdCents) {
      state = 'goal';
      text = this.#templates.goal;
    } else {
      state = 'progress';
      text = this.#templates.progress.replace('[remaining]', this.#formatMoney(remainingCents));
    }

    this.dataset.state = state;
    if (this.#textEl) this.#textEl.textContent = text;
    if (this.#fillEl) this.#fillEl.style.width = `${ratio * 100}%`;
    if (this.#trackEl) this.#trackEl.setAttribute('aria-valuenow', Math.round(ratio * 100));
  }

  #formatMoney(cents) {
    const formatted = typeof window.Shopify?.formatMoney === 'function'
      ? window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}')
      : `$${(cents / 100).toFixed(2)}`;
    // Strip trailing .00 or ,00 (Liquid `money_without_trailing_zeros` equivalent).
    return formatted.replace(/([.,])00(\D|$)/, '$2');
  }
}

if (!customElements.get('progress-bar')) {
  customElements.define('progress-bar', ProgressBar);
}
